import React, { useMemo } from 'react';
import { GraphMember, Relationship, Side } from '@/contexts/FamilyGraphContext';
import { AlertCircle, Plus, UserPlus } from 'lucide-react';

interface Gap {
  id: string;
  /** What relationship is missing — drives icon + add intent. */
  relationship: Relationship;
  side: Side;
  /** Headline shown in the prompt card. */
  title: string;
  /** Explanatory subtext — short, action-oriented. */
  body: string;
  /** Tone gradient for the avatar circle. */
  tone: string;
  /** Recommended primary action: 'add' (open AddMember) or 'invite' (open invite). */
  action: 'add' | 'invite';
}

interface Props {
  members: GraphMember[];
  /** Open the Add Relative modal pre-filled to (relationship, side). */
  onAdd: (input: { relationship: Relationship; side: Side }) => void;
  /** Open the Invite modal pre-filled with a relationship purpose. */
  onInvite: (input?: { relation?: string; relName?: string }) => void;
}

/**
 * MissingBranches — surfaces concrete, contextual gaps in the user's tree.
 *
 * Heuristics (additive, never blocking):
 *  - No mother / father → "Add your mother/father to complete your core family."
 *  - No sibling                 → "Add a sibling — they're often closest to you."
 *  - No grandparents on a side  → "Add grandparents on Mom's/Dad's side."
 *  - Parent has no parents      → "Add {parentName}'s parents to extend the tree."
 *  - Has cousins but no aunts/uncles → "Connect this branch — invite your cousin."
 *  - 3+ relatives, 0 invites    → suggested invite (handled by the empty state).
 *
 * Returns null when the tree already covers the basics — keeps the UI quiet
 * once the user has built a healthy branch.
 */
const MissingBranches: React.FC<Props> = ({ members, onAdd, onInvite }) => {
  const gaps: Gap[] = useMemo(() => {
    const approved = members.filter((m) => m.approvalStatus !== 'rejected');
    const hasMother = approved.some(
      (m) => m.relationship === 'mother' || m.relationship === 'step-parent' || m.relationship === 'adopted-parent'
    );
    const hasFather = approved.some(
      (m) => m.relationship === 'father' || (m.relationship === 'step-parent' && m.side === 'father')
    );
    const hasSibling = approved.some((m) => m.relationship === 'sibling');
    const hasMaternalGP = approved.some(
      (m) => m.relationship === 'grandparent' && (m.side === 'mother' || m.side === 'both')
    );
    const hasPaternalGP = approved.some(
      (m) => m.relationship === 'grandparent' && (m.side === 'father' || m.side === 'both')
    );
    const hasChild = approved.some((m) => m.relationship === 'child');
    const hasCousin = approved.some((m) => m.relationship === 'cousin');
    const hasAuntUncle = approved.some((m) => m.relationship === 'aunt-uncle');

    const out: Gap[] = [];

    if (!hasMother) {
      out.push({
        id: 'missing-mother',
        relationship: 'mother',
        side: 'mother',
        title: 'Add your mother',
        body: 'Complete your core family — start your maternal branch.',
        tone: 'from-pink-500 to-rose-500',
        action: 'add',
      });
    }
    if (!hasFather) {
      out.push({
        id: 'missing-father',
        relationship: 'father',
        side: 'father',
        title: 'Add your father',
        body: 'Complete your core family — start your paternal branch.',
        tone: 'from-sky-500 to-blue-500',
        action: 'add',
      });
    }
    if (!hasSibling && (hasMother || hasFather)) {
      out.push({
        id: 'missing-sibling',
        relationship: 'sibling',
        side: 'self',
        title: 'Add a sibling',
        body: 'Sisters and brothers anchor your generation in the tree.',
        tone: 'from-emerald-500 to-teal-500',
        action: 'add',
      });
    }
    if (hasMother && !hasMaternalGP) {
      out.push({
        id: 'missing-maternal-gp',
        relationship: 'grandparent',
        side: 'mother',
        title: "Add grandparents on Mom's side",
        body: 'Extend your maternal branch up one generation.',
        tone: 'from-fuchsia-500 to-purple-500',
        action: 'add',
      });
    }
    if (hasFather && !hasPaternalGP) {
      out.push({
        id: 'missing-paternal-gp',
        relationship: 'grandparent',
        side: 'father',
        title: "Add grandparents on Dad's side",
        body: 'Extend your paternal branch up one generation.',
        tone: 'from-indigo-500 to-blue-500',
        action: 'add',
      });
    }
    if (hasCousin && !hasAuntUncle) {
      out.push({
        id: 'missing-aunt-uncle',
        relationship: 'aunt-uncle',
        side: 'both',
        title: 'Connect this branch — invite your cousin',
        body: 'You have cousins listed but no aunts/uncles. Invite a cousin to fill in the link.',
        tone: 'from-amber-500 to-orange-500',
        action: 'invite',
      });
    }
    if (!hasChild && approved.length >= 3) {
      out.push({
        id: 'missing-child',
        relationship: 'child',
        side: 'self',
        title: 'Add your children',
        body: 'Pass your story forward — record the next generation.',
        tone: 'from-rose-500 to-pink-500',
        action: 'add',
      });
    }
    return out.slice(0, 4);
  }, [members]);

  if (gaps.length === 0) return null;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-amber-50 border-2 border-rose-100 p-5">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="w-5 h-5 text-rose-500" />
        <h3 className="font-serif font-bold text-lg text-slate-900">
          Gaps in your tree
        </h3>
        <span className="ml-auto text-xs font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
          {gaps.length} missing
        </span>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Filling these in helps your family see how everyone connects.
      </p>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {gaps.map((g) => (
          <div
            key={g.id}
            className="flex items-center gap-3 rounded-xl bg-white border border-rose-100 p-3 shadow-sm"
          >
            <div
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${g.tone} text-white flex items-center justify-center shrink-0 shadow-sm`}
            >
              <UserPlus className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-900 leading-tight">
                {g.title}
              </div>
              <div className="text-[11px] text-slate-600 leading-tight mt-0.5">
                {g.body}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (g.action === 'invite') {
                  onInvite({ relation: g.relationship });
                } else {
                  onAdd({ relationship: g.relationship, side: g.side });
                }
              }}
              className="shrink-0 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              {g.action === 'invite' ? 'Invite' : 'Add'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MissingBranches;
